import { useState } from 'react';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Spacing from 'components/Spacing';
import FormWrapper from './FormWrapper';
import PageSection from 'components/PageSection';
import Typography from '@mui/material/Typography';
import RevaluationForm from './CreateRevaluation';
import { createLpCompanyEditRoute } from 'adminConstants/routes';
import { RevaluationType } from 'further-types/lp/revaluation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Grid } from '@mui/material';
import { Step, StepLabel, Stepper } from '@mui/material';
import GridContainer from 'components/GridContainer';
import ErrorBoundary from 'components/ErrorBoundary';
import FullPageSuccess from 'components/FullPageSuccess';
import ReviewRevaluation from './ReviewRevaluation';
import { useRevaluationForm } from './hooks/useRevaluationForm';
import { Revaluation, InvestmentHistory } from './types';
import { AssetFullHistoryItem } from './FormWrapper';
import { HoldingClassInvestmentTotal } from './CreateRevaluation/types';
import { Vehicle } from 'further-types/lp/vehicle';

enum Steps {
  Create,
  Review,
  Success,
}

const stepLabels = {
  [Steps.Create]: 'Create Revaluation',
  [Steps.Review]: 'Review & Submit',
};

type Asset = {
  name: string;
  baseCurrency: CurrencyCode;
};

type RevaluationsFormContainerProps = {
  id: string;
  pageTitle: string;
  breadcrumbs: Array<{ label: string; link?: string }>;
  vehicle: Vehicle;
  asset: Asset;
  revaluations: Array<Omit<Revaluation, 'isNew'>>;
  investmentHistory: InvestmentHistory;
  assetFullHistory: Record<string, Array<AssetFullHistoryItem>>;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
};

const RevaluationsFormContainer: React.FC<RevaluationsFormContainerProps> = ({
  id,
  pageTitle,
  breadcrumbs,
  vehicle,
  asset,
  revaluations,
  investmentHistory,
  assetFullHistory,
  holdingClassInvestmentTotals,
}) => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.Create);

  // Build newRevaluations from holdingClassInvestmentTotals (all holding classes)
  const newRevaluations = holdingClassInvestmentTotals.map(
    (holdingClassInvestmentTotal) => {
      const holdingClassId = holdingClassInvestmentTotal.holdingClassId;
      const holdingClassHistory = assetFullHistory[holdingClassId] ?? [];

      return {
        id: holdingClassId,
        revaluationId: undefined,
        holdingClassId,
        type: RevaluationType.Revaluation as const,
        assetId: id,
        holdingClassName: holdingClassHistory[0]?.holdingClassName ?? '',
        unitPriceTcy: holdingClassInvestmentTotal.latestUnitPrice ?? 0,
        currentHoldValue: holdingClassInvestmentTotal.currentHoldValue ?? 0,
        totalNumberOfUnits: holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        currentNumberOfUnits:
          holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        ledgerDate: new Date(),
        currency:
          holdingClassInvestmentTotal.currency ??
          asset.baseCurrency ??
          CurrencyCode.GBP,
        isRemoved: false,
        isNew: true,
      };
    },
  );

  const formMethods = useRevaluationForm({
    investmentHistory,
    revaluations,
    asset,
    newRevaluations,
  });

  const handleNextStep = () => {
    setActiveStep(Steps.Review);
  };

  const handleSubmit = () => {
    setActiveStep(Steps.Success);
  };

  const handlePreviousStep = () => {
    setActiveStep(Steps.Create);
  };

  if (activeStep === Steps.Success) {
    return (
      <PageContainer heading={pageTitle} breadcrumbs={breadcrumbs}>
        <FullPageSuccess
          title="Revaluation has been created successfully"
          additionalText="The asset and general ledger have been updated and this new revaluation will feed into data across the system."
          proceedText="Return to asset"
          proceedLink={createLpCompanyEditRoute(id)}
          secondaryText="View all assets"
          secondaryLink={'/assets'}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer heading={pageTitle} breadcrumbs={breadcrumbs}>
      <PageContent>
        <Spacing size="lg">
          <GridContainer>
            <Grid size={{ sm: 12, md: 12, lg: 8 }}>
              <Stepper activeStep={activeStep}>
                {Object.values(stepLabels).map((stepLabel) => (
                  <Step key={stepLabel}>
                    <StepLabel>{stepLabel}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          </GridContainer>

          <FormWrapper
            formMethods={formMethods}
            externalState={{
              selectedVehicle: vehicle,
              assetFullHistory,
            }}
          >
            {activeStep === Steps.Create && (
              <ErrorBoundary>
                <PageSection
                  title={`${vehicle.vehicleName}'s holding in ${asset.name}`}
                >
                  <Typography variant="body1">
                    {`Enter ${asset.name}'s total asset value for asset-wide revaluation,
                      or select a holding class and revalue ${asset.name} by class.`}
                  </Typography>
                </PageSection>
                <RevaluationForm
                  assetName={asset.name}
                  assetCurrency={asset.baseCurrency}
                  assetId={id}
                  vehicleId={vehicle._id}
                  vehicle={vehicle}
                  holdingClassInvestmentTotals={holdingClassInvestmentTotals}
                  onSuccess={handleNextStep}
                />
              </ErrorBoundary>
            )}

            {activeStep === Steps.Review && (
              <ReviewRevaluation
                handlePreviousStep={handlePreviousStep}
                handleSubmit={handleSubmit}
                vehicle={vehicle}
                assetName={asset.name}
                assetId={id}
              />
            )}
          </FormWrapper>
        </Spacing>
      </PageContent>
    </PageContainer>
  );
};

export default RevaluationsFormContainer;
